/**
 * The pre-export invariant gate. The hard requirement is that an exported file must never be
 * corrupt or rejected, so export runs this and refuses to download on any violation. This is a
 * core subset of the plan's full suite (the rest lands in Pass 2), chosen to catch gross errors
 * and — crucially — any drift in records we never meant to touch.
 *
 * Shared verbatim between tests and the in-app gate.
 */
import type { DataRecord, FitFile, FitRecord } from '../codec/types'
import { GLOBAL, LEN, LENGTH_TYPE, LAP, SES, getNumber } from './fields'
import { buildActivity } from './model'

/** Globals we legitimately rewrite (session, lap, length) or drop (best-effort). */
const EDITABLE = new Set<number>([GLOBAL.session, GLOBAL.lap, GLOBAL.length, GLOBAL.bestEffort])

export interface Violation {
  readonly check: string
  readonly detail: string
}

function idleCount(file: FitFile): number {
  return file.records.filter(
    (r) =>
      r.kind === 'data' &&
      r.def.globalMessageNumber === GLOBAL.length &&
      getNumber(r, LEN.lengthType) === LENGTH_TYPE.idle,
  ).length
}

/** Records that must pass through untouched, in stream order (excludes editable + dropped g113). */
function untouched(file: FitFile): FitRecord[] {
  return file.records.filter((r) => !(r.kind === 'data' && EDITABLE.has(r.def.globalMessageNumber)))
}

function rawEqual(a: Uint8Array | null, b: Uint8Array | null): boolean {
  if (a === null || b === null) return false
  return a.length === b.length && a.every((x, i) => x === b[i])
}

export function validate(original: FitFile, output: FitFile): Violation[] {
  const v: Violation[] = []
  const add = (check: string, detail: string) => v.push({ check, detail })

  // 0. The output must form a coherent activity at all.
  let activity
  try {
    activity = buildActivity(output)
  } catch (e) {
    add('structure', e instanceof Error ? e.message : String(e))
    return v // nothing else is meaningful if the shape is broken
  }

  const lengths = output.records.filter(
    (r): r is DataRecord => r.kind === 'data' && r.def.globalMessageNumber === GLOBAL.length,
  )
  const laps = output.records.filter(
    (r): r is DataRecord => r.kind === 'data' && r.def.globalMessageNumber === GLOBAL.lap,
  )
  const session = output.records.find(
    (r): r is DataRecord => r.kind === 'data' && r.def.globalMessageNumber === GLOBAL.session,
  )

  // 1. Untouched-set: every non-editable record byte-identical to source, same order and count.
  const origU = untouched(original)
  const outU = untouched(output)
  if (origU.length !== outU.length) {
    add(
      'untouched-count',
      `${origU.length} non-editable records in source, ${outU.length} in output`,
    )
  } else {
    for (let i = 0; i < origU.length; i++) {
      if (!rawEqual(origU[i]!.raw, outU[i]!.raw)) {
        add(
          'untouched-bytes',
          `non-editable record #${i} (global ${outU[i]!.def.globalMessageNumber}) changed`,
        )
        break
      }
    }
  }

  // 2. Conservation law: Σ length elapsed == session.total_timer_time.
  const sumElapsed = lengths.reduce((n, r) => n + (getNumber(r, LEN.totalElapsed) ?? 0), 0)
  const timer = session ? getNumber(session, SES.totalTimer) : undefined
  if (timer !== sumElapsed) {
    add('conservation', `Σ length elapsed ${sumElapsed} != session.total_timer_time ${timer}`)
  }

  // 3. Idle-length count is preserved (our ops never create or destroy rests).
  if (idleCount(original) !== idleCount(output)) {
    add(
      'idle-count',
      `${idleCount(original)} idle lengths in source, ${idleCount(output)} in output`,
    )
  }

  // 4. Per length: elapsed == timer, and message_index is contiguous 0..n-1.
  lengths.forEach((r, i) => {
    if (getNumber(r, LEN.totalElapsed) !== getNumber(r, LEN.totalTimer)) {
      add('length-elapsed-timer', `length #${i}: total_elapsed != total_timer`)
    }
    if (getNumber(r, LEN.messageIndex) !== i) {
      add('length-index', `length #${i}: message_index ${getNumber(r, LEN.messageIndex)}`)
    }
  })

  // 5. Per lap: num_lengths == num_active == member active count; fli monotonic; contiguous index.
  let expectFirst = 0
  activity.stints.forEach((stint, i) => {
    const active = stint.lengths.filter((l) => l.active).length
    const numLengths = getNumber(stint.lapRecord, LAP.numLengths)
    const numActive = getNumber(stint.lapRecord, LAP.numActiveLengths)
    if (numLengths !== active || numActive !== active) {
      add(
        'lap-count',
        `lap #${i}: num_lengths=${numLengths}, num_active=${numActive}, actual active=${active}`,
      )
    }
    const fli = getNumber(stint.lapRecord, LAP.firstLengthIndex)
    if (fli !== expectFirst) {
      add('lap-fli', `lap #${i}: first_length_index ${fli}, expected ${expectFirst}`)
    }
    expectFirst += stint.lengths.length
    if (getNumber(stint.lapRecord, LAP.messageIndex) !== i) {
      add('lap-index', `lap #${i}: message_index ${getNumber(stint.lapRecord, LAP.messageIndex)}`)
    }
  })
  if (expectFirst !== lengths.length) {
    add('lap-coverage', `laps cover ${expectFirst} lengths, file has ${lengths.length}`)
  }

  // 6. Session length counts match the active total.
  const totalActive = activity.lengths.filter((l) => l.active).length
  if (session) {
    if (
      getNumber(session, SES.numLengths) !== totalActive ||
      getNumber(session, SES.numActiveLengths) !== totalActive
    ) {
      add('session-count', `session num_lengths/active != ${totalActive}`)
    }
  }

  return v
}
